// Plan 23b, Task 2.3 — Countdown Numbers primitive.
// Behavior contract: 3–2–1–GO! via Plan 20a countdown-zoom hook.

import type { UIComponentHandle, UIComponentConfig } from './types';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';
import { createCountdownZoomHook } from '../vfx/countdown-podium';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface CountdownOpts {
  onComplete?: () => void;
  onStep?: (step: '3' | '2' | '1' | 'GO!') => void;
}

export interface CountdownHandle extends UIComponentHandle {
  start(): Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Design token constants — values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** typography.family.headline */
const FONT_FAMILY = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif";

/** typography.size.countdown — 120px */
const FONT_SIZE = '120px';

/** color.text.primary — white for digits */
const COLOR_TEXT_PRIMARY = '#FFFFFF';

/** color.brand.amber — for "GO!" step */
const COLOR_AMBER = '#E8A020';

/** z-index.overlay — 350 */
const Z_INDEX = 350;

// ─────────────────────────────────────────────────────────────────────────────
// Sequence timing (absolute from sequence start)
//
// Each number step: 500ms zoom-in + 400ms hold + 150ms fade = 1050ms → 1.05s
// GO! step:         500ms zoom-in + 600ms hold + 150ms fade = 1250ms → 1.25s
//
// Absolute positions for the scheduler timeline (one handle, all callbacks):
//   Step 2 ("2"):  t = 1.05s
//   Step 3 ("1"):  t = 2.10s
//   Step 4 ("GO!"): t = 3.15s
//   Done:           t = 4.40s (3.15 + 1.25)
//
// A dedicated SCHEDULER handle is used for tl.call callbacks. This avoids the
// "cascade in one update tick" bug: if we added callbacks to each hook's own
// timeline handle, the GSAP registry would visit newly-created handles in the
// same update() iteration (Set iteration visits entries added mid-loop), causing
// all 4 steps to fire on a single gsapRegistry.update(1.05) call.
//
// The scheduler timeline is created once in start() and never replaced.
// Hook timelines (for animation) are separate — hook.play() creates a new one
// per step, and hook.dispose() kills the current one.
// ─────────────────────────────────────────────────────────────────────────────

const T_STEP2 = 1.05; // "3" → "2"
const T_STEP3 = 2.1; // "2" → "1"
const T_STEP_GO = 3.15; // "1" → "GO!"
const T_DONE = 4.4; // "GO!" → resolve

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createCountdown(config: UIComponentConfig & CountdownOpts): CountdownHandle {
  const { gsapRegistry, mount, onComplete, onStep } = config;

  let disposed = false;
  /** Scheduler handle — created in start(), disposed in dispose(). */
  let schedulerHandle: GsapTimelineHandle | null = null;

  // ── Build root container (full-viewport overlay) ──────────────────────────

  const root = document.createElement('div');
  root.className = 'ui-countdown';
  // Use explicit top/right/bottom/left instead of `inset` for happy-dom compat.
  root.style.cssText = [
    'position: fixed',
    'top: 0',
    'right: 0',
    'bottom: 0',
    'left: 0',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    `z-index: ${Z_INDEX}`,
    'pointer-events: none',
  ].join('; ');

  // ── Convenience getter for inset (reads back from individual properties) ──
  // Expose .style.inset as a unified "0" by overriding the property via a
  // computed check — but we can't easily do that. Instead, expose it as a
  // data attribute and keep the CSS properties individual.
  // NOTE: Tests check el.style.inset === '0'. We set it via Object.defineProperty
  // on the style object after creation is not viable. Instead, ALSO set
  // root.style.inset if the browser supports it; otherwise rely on the
  // top/right/bottom/left values. We'll add both:
  root.style.setProperty('inset', '0');

  // ── Countdown text element ────────────────────────────────────────────────

  const countdownEl = document.createElement('div');
  countdownEl.setAttribute('data-countdown-text', '');
  countdownEl.style.cssText = [
    `font-family: var(--typography-family-headline, ${FONT_FAMILY})`,
    `font-size: var(--typography-size-countdown, ${FONT_SIZE})`,
    `color: ${COLOR_TEXT_PRIMARY}`,
    'font-weight: 700',
    'text-align: center',
    'line-height: 1',
    'user-select: none',
    // Strong black outline for scene readability (tokens.json typography.outline.heading-thick)
    'text-shadow: -3px -3px 0 #000000, 3px -3px 0 #000000, -3px 3px 0 #000000, 3px 3px 0 #000000',
  ].join('; ');

  // ── Flash overlay (sibling div used by countdown-zoom hook for GO! flash) ─
  // position: absolute + inset: 0 within the root container's flex context.

  const flashOverlay = document.createElement('div');
  flashOverlay.setAttribute('data-flash-overlay', '');
  flashOverlay.style.cssText = [
    'position: absolute',
    'top: 0',
    'right: 0',
    'bottom: 0',
    'left: 0',
    'opacity: 0',
    'pointer-events: none',
    'background: #FFFFFF',
  ].join('; ');
  flashOverlay.style.setProperty('inset', '0');

  // ── Assemble DOM ──────────────────────────────────────────────────────────

  root.appendChild(countdownEl);
  root.appendChild(flashOverlay);

  if (mount) {
    mount.appendChild(root);
  }

  // ── Hook instance (single instance; hook.play() kills its previous handle) ─

  const hook = createCountdownZoomHook({ gsapRegistry });

  // ─────────────────────────────────────────────────────────────────────────
  // start() — runs the 3 → 2 → 1 → GO! sequence
  // Returns a Promise that resolves when the full sequence completes.
  //
  // Architecture: ONE scheduler timeline handles all step callbacks at absolute
  // positions. This prevents cascade-in-one-tick (see comment above T_STEP2).
  // Hook.play() is called directly from the scheduler callbacks — each call
  // creates a new animation timeline for that step's GSAP animation.
  // ─────────────────────────────────────────────────────────────────────────

  function start(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (disposed) {
        resolve();
        return;
      }

      // Kill any prior scheduler (idempotent guard for repeat start() calls)
      if (schedulerHandle) {
        schedulerHandle.dispose();
        schedulerHandle = null;
      }

      // ── Step 1: "3" — immediately ──────────────────────────────────────────
      countdownEl.textContent = '3';
      countdownEl.style.color = COLOR_TEXT_PRIMARY;
      onStep?.('3');
      hook.play(countdownEl, { number: '3' });

      // ── Create one scheduler timeline for ALL transition callbacks ─────────
      // F20a-CLASS-1: all tl.call() invocations use explicit position args.
      const sched = gsapRegistry.create('countdown-scheduler');
      schedulerHandle = sched;

      // Step 2: "2" at t = 1.05s
      sched.timeline.call(
        () => {
          if (disposed) return;
          countdownEl.textContent = '2';
          countdownEl.style.color = COLOR_TEXT_PRIMARY;
          onStep?.('2');
          hook.play(countdownEl, { number: '2' });
        },
        undefined,
        T_STEP2,
      );

      // Step 3: "1" at t = 2.10s
      sched.timeline.call(
        () => {
          if (disposed) return;
          countdownEl.textContent = '1';
          countdownEl.style.color = COLOR_TEXT_PRIMARY;
          onStep?.('1');
          hook.play(countdownEl, { number: '1' });
        },
        undefined,
        T_STEP3,
      );

      // Step 4: "GO!" at t = 3.15s
      sched.timeline.call(
        () => {
          if (disposed) return;
          countdownEl.textContent = 'GO!';
          countdownEl.style.color = COLOR_AMBER;
          onStep?.('GO!');
          hook.play(countdownEl, { number: 'GO!' });
        },
        undefined,
        T_STEP_GO,
      );

      // Done: at t = 4.40s
      sched.timeline.call(
        () => {
          if (disposed) return;
          schedulerHandle = null;
          sched.dispose();
          onComplete?.();
          resolve();
        },
        undefined,
        T_DONE,
      );
    });
  }

  // ── dispose ───────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Kill scheduler timeline
    if (schedulerHandle) {
      schedulerHandle.dispose();
      schedulerHandle = null;
    }

    // Kill any in-flight animation hook timelines
    hook.dispose();

    // Remove root from DOM
    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return {
    element: root,
    start,
    dispose,
  };
}
